import { Box, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import CXDataGridManageColumnsButton from 'common/components/cx-data-grid/cx-data-grid-manage-columns-button.component';
import CXDataGrid from 'common/components/cx-data-grid/cx-data-grid.component';
import { CommonStyledLink } from 'common/components/links/styles/common-link.style';
import { PersonLink } from 'common/components/person-link/person-link.component';
import { useDrawer } from 'common/hooks';
import AppRoutesEnum from 'common/routes/AppRoutes.enum';
import {
  findSkillsSortedByRarity,
  GridCellItem,
  MATCHING_ENGINE_PERSON_DRAWER_ID,
  MatchingEngineContributorDetailDrawer,
  PEOPLE_COMPARISON_COLUMN_FIELDS,
  SkillStatus,
} from 'features/matching-engine/matching-engine.utils';
import { useRef, useState } from 'react';

import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import { ROW_HEIGHT, SLOTS } from 'features/profile-data-grid/profile-data-grid.constants';

function getSkillStatusLabel(person: any, skill: any): string {
  const isVerified = person.verifiedSkills.some((s: any) => s.id === skill.id);

  if (isVerified) {
    return person.lastFeedbackDate || SkillStatus.VERIFIED;
  }

  const isMaybe = person.skills.some((s: any) => s.id === skill.id);
  if (isMaybe) {
    return SkillStatus.MAYBE;
  }

  return SkillStatus.MISSING;
}

function getCommonStyledLink(staticDataType: string, staticDataID: any, valueToRender: any) {
  return (
    <GridCellItem
      readonly={true}
      value={
        <CommonStyledLink
          color="inherit"
          sx={{ fontWeight: 500 }}
          href={AppRoutesEnum.STATIC_DATA_SUMMARY}
          params={{
            staticDataType: `${staticDataType}`,
            staticDataId: `${staticDataID}`,
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          variant="body4"
        >
          {valueToRender}
        </CommonStyledLink>
      }
    />
  );
}

function useMatchingEngineDrawer() {
  const { openDrawer } = useDrawer();

  return (
    personData: any,
    skills: any[],
    initialTab: MatchingEngineContributorDetailDrawer,
  ) => {
    openDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID, {
      person: {
        name: personData.fullName,
        photo: personData.photo,
        profileID: personData.id,
        stepID: null,
        feedbacks: personData.feedbacks,
        skillID: skills.map((skill) => skill.id),
      },
      initialTab,
    });
  };
}

type PeopleComparisonGridProps = {
  skills: any[];
  people: any[];
};

export const PeopleComparsionGrid: React.FC<PeopleComparisonGridProps> = ({
  skills,
  people,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const { palette } = useTheme();
  const openMatchingEngineDrawer = useMatchingEngineDrawer();

  const columns = [
    {
      field: 'attribute',
      headerName: 'Attribute',
      width: 200,
      disableColumnMenu: true,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.isGroupHeader) {
          const arrowIcon = groupExpandedState[row.id] ? (
            <KeyboardArrowDownOutlinedIcon />
          ) : (
            <KeyboardArrowRightOutlinedIcon />
          );
          return (
            <Box
              sx={{
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'auto',
                width: '100%',
                height: '100%',
                py: 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleGroupExpansion(row.id);
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mr: 1,
                }}
              >
                {arrowIcon}
              </Box>
              {row.attribute}
            </Box>
          );
        }
        return row.attribute;
      },
    },
    ...people.map((person) => ({
      field: person.fullName,
      headerName: person.fullName,
      width: 225,
      minWidth: 180,
      flex: 1,
      sortable: false,
      filterable: false,
      groupable: false,
      aggregable: false,
      renderHeader: () =>
        person.id ? (
          <PersonLink
            key={person.id}
            id={person.id}
            name={person.fullName}
            color="inherit"
            fontWeight={500}
          />
        ) : (
          ''
        ),
      renderCell: ({ row }: { row: any }) => {
        const cellValue = row[person.fullName];
        return cellValue;
      },
    })),
    {
      field: 'gridActions',
      headerClassName: 'gridActions',
      renderHeader: () => <CXDataGridManageColumnsButton />,
      width: 40,
      minWidth: 40,
      sortable: false,
      resizable: false,
      pinnable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
  ] as GridColDef[];

  const getMatchedSkillsCounter = (personData: any) => {
    const matchedSkillsTracker = {
      confirmed: personData.verifiedSkills.length,
      maybe: personData.maybeSkills.length,
      missing: personData.missingCount,
    };
    return matchedSkillsTracker;
  };

  const getRawValue = (personData: any, attribute: string) => {
    switch (attribute) {
      case PEOPLE_COMPARISON_COLUMN_FIELDS.CRITICAL_CURRENT_ALLOCATION:
        return personData.allocationMetadata?.highCriticalityCount;
      case PEOPLE_COMPARISON_COLUMN_FIELDS.MAJOR_CURRENT_ALLOCATION:
        return personData.allocationMetadata?.mediumCriticalityCount;
      case PEOPLE_COMPARISON_COLUMN_FIELDS.LOW_CURRENT_ALLOCATION:
        return personData.allocationMetadata?.lowCriticalityCount;

      case PEOPLE_COMPARISON_COLUMN_FIELDS.CONFIRMED_SKILLS:
        return getMatchedSkillsCounter(personData).confirmed;

      case PEOPLE_COMPARISON_COLUMN_FIELDS.MAYBE_SKILLS:
        return getMatchedSkillsCounter(personData).maybe;

      case PEOPLE_COMPARISON_COLUMN_FIELDS.MISSING_SKILLS:
        return getMatchedSkillsCounter(personData).missing;

      case PEOPLE_COMPARISON_COLUMN_FIELDS.MANAGER:
        return personData.managers?.manager?.fullName;
      case PEOPLE_COMPARISON_COLUMN_FIELDS.L2MANAGER:
        return personData.managers?.l2Manager?.fullName;
      case PEOPLE_COMPARISON_COLUMN_FIELDS.L3MANAGER:
        return personData.managers?.l3Manager?.fullName;

      case PEOPLE_COMPARISON_COLUMN_FIELDS.LOCATION:
        return personData.workLocation?.displayName;
      case PEOPLE_COMPARISON_COLUMN_FIELDS.DEPARTMENT:
        return personData.department?.displayName;
      case PEOPLE_COMPARISON_COLUMN_FIELDS.DIVISION:
        return personData.division?.displayName;
      case PEOPLE_COMPARISON_COLUMN_FIELDS.PRODUCT:
        return personData.product?.displayName;

      default:
        return personData[attribute] ?? '-';
    }
  };

  function getStyledComponent(
    personData: any,
    attribute: string,
    valueToRender: any,
  ): React.ReactElement {
    switch (attribute) {
      case PEOPLE_COMPARISON_COLUMN_FIELDS.CRITICAL_CURRENT_ALLOCATION:
      case PEOPLE_COMPARISON_COLUMN_FIELDS.MAJOR_CURRENT_ALLOCATION:
      case PEOPLE_COMPARISON_COLUMN_FIELDS.LOW_CURRENT_ALLOCATION:
        return (
          <GridCellItem
            value={valueToRender}
            readonly={false}
            onClick={() =>
              openMatchingEngineDrawer(
                personData,
                skills,
                MatchingEngineContributorDetailDrawer.CurrentAllocation,
              )
            }
          />
        );

      case PEOPLE_COMPARISON_COLUMN_FIELDS.MANAGER:
        return (
          <GridCellItem
            readonly={true}
            value={
              personData.managers?.manager?.id ? (
                <PersonLink
                  key={personData.managers?.manager?.id}
                  id={personData.managers?.manager?.id}
                  name={valueToRender}
                  color="inherit"
                  fontWeight={500}
                />
              ) : (
                ''
              )
            }
          />
        );
      case PEOPLE_COMPARISON_COLUMN_FIELDS.L2MANAGER:
        return (
          <GridCellItem
            readonly={true}
            value={
              personData.managers?.l2Manager?.id ? (
                <PersonLink
                  key={personData.managers?.l2Manager?.id}
                  id={personData.managers?.l2Manager?.id}
                  name={valueToRender}
                  color="inherit"
                  fontWeight={500}
                />
              ) : (
                ''
              )
            }
          />
        );
      case PEOPLE_COMPARISON_COLUMN_FIELDS.L3MANAGER:
        return (
          <GridCellItem
            readonly={true}
            value={
              personData.managers?.l3Manager?.id ? (
                <PersonLink
                  key={personData.managers?.l3Manager?.id}
                  id={personData.managers?.l3Manager?.id}
                  name={valueToRender}
                  color="inherit"
                  fontWeight={500}
                />
              ) : (
                ''
              )
            }
          />
        );
      case PEOPLE_COMPARISON_COLUMN_FIELDS.LOCATION:
        return getCommonStyledLink(
          'WORK_LOCATION',
          personData.workLocation?.id,
          valueToRender,
        );
      case PEOPLE_COMPARISON_COLUMN_FIELDS.DEPARTMENT:
        return getCommonStyledLink('DEPARTMENT', personData.department?.id, valueToRender);
      case PEOPLE_COMPARISON_COLUMN_FIELDS.DIVISION:
        return getCommonStyledLink('DIVISION', personData.division?.id, valueToRender);
      case PEOPLE_COMPARISON_COLUMN_FIELDS.PRODUCT:
        return getCommonStyledLink('PRODUCT', personData.product?.id, valueToRender);
      default:
        return <GridCellItem readonly={false} value={valueToRender} />;
    }
  }

  const skillsSortedByRarity = findSkillsSortedByRarity(skills, people);

  // To construct the transpose grid of SkillFilterGrid, this exact order is required.
  const getComparisonRows = () => {
    const rows: any[] = [];
    let rowIndex = 0;

    skillsSortedByRarity.forEach((skill) => {
      const row: any = {
        id: `skill_${skill.id}`,
        attribute: skill.name ?? 'Unnamed Skill',
        groupId: null,
      };

      people.forEach((personData) => {
        const personName = personData.fullName;
        row[personName] = (
          <GridCellItem
            readonly={false}
            value={`${getSkillStatusLabel(personData, skill)}`}
          />
        );
      });

      rows.push(row);
    });

    const createGroupRowBundle = (groupName: string, fields: string[]) => {
      const groupRowId = `group_${groupName.replace(/\s+/g, '_').toLowerCase()}`;
      const groupHeaderRow: any = {
        id: groupRowId,
        attribute: groupName,
        isGroupHeader: true,
        groupId: groupRowId,
      };

      if (groupName === 'CURRENT ALLOCATIONS') {
        people.forEach((personData) => {
          const personName = personData.fullName;
          let total = 0;
          fields.forEach((key) => {
            const val = getRawValue(personData, key);
            total += val ?? 0;
          });
          groupHeaderRow[personName] = <GridCellItem readonly={false} value={`${total}`} />;
        });
      }

      rows.push(groupHeaderRow);

      fields.forEach((key) => {
        const row: any = {
          id: `base_${rowIndex++}`,
          groupId: groupRowId,
        };
        row['attribute'] = <Box pl={4}>{key}</Box>;
        people.forEach((personData) => {
          const personName = personData.fullName;
          const valueToRender = getRawValue(personData, key);
          const renderedValue = getStyledComponent(personData, key, valueToRender);
          row[personName] = renderedValue;
        });
        rows.push(row);
      });
    };

    createGroupRowBundle('CURRENT ALLOCATIONS', [
      PEOPLE_COMPARISON_COLUMN_FIELDS.CRITICAL_CURRENT_ALLOCATION,
      PEOPLE_COMPARISON_COLUMN_FIELDS.MAJOR_CURRENT_ALLOCATION,
      PEOPLE_COMPARISON_COLUMN_FIELDS.LOW_CURRENT_ALLOCATION,
    ]);

    const NON_GROUPED_COMPARISON_COLUMN_FIELDS = [
      PEOPLE_COMPARISON_COLUMN_FIELDS.MANAGER,
      PEOPLE_COMPARISON_COLUMN_FIELDS.L2MANAGER,
      PEOPLE_COMPARISON_COLUMN_FIELDS.L3MANAGER,
      PEOPLE_COMPARISON_COLUMN_FIELDS.DEPARTMENT,
      PEOPLE_COMPARISON_COLUMN_FIELDS.DIVISION,
      PEOPLE_COMPARISON_COLUMN_FIELDS.LOCATION,
      PEOPLE_COMPARISON_COLUMN_FIELDS.PRODUCT,
    ];

    NON_GROUPED_COMPARISON_COLUMN_FIELDS.forEach((key) => {
      const row: any = {
        id: `remaining_${rowIndex++}`,
        attribute: key,
        groupId: null, // No group for these rows
      };
      people.forEach((personData) => {
        const personName = personData.fullName;
        const valueToRender = getRawValue(personData, key);
        const renderedValue = getStyledComponent(personData, key, valueToRender);
        row[personName] = renderedValue;
      });
      rows.push(row);
    });

    return rows;
  };

  const comparisonRows = getComparisonRows();

  type GroupedRecord = {
    [key: string]: boolean;
  };

  const initialGroupedRecords = comparisonRows
    .filter((row) => row.isGroupHeader)
    .reduce((accumulator, groupRow) => {
      accumulator[groupRow.id] = true; // expanded initially
      return accumulator;
    }, {} as GroupedRecord);

  const [groupExpandedState, setGroupExpandedState] = useState(initialGroupedRecords);

  const toggleGroupExpansion = (groupId: string) => {
    setGroupExpandedState((prev: GroupedRecord) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const updatedRows = comparisonRows.filter((row) => {
    if (row.isGroupHeader) {
      return true;
    }

    if (!row.groupId) {
      return true;
    }
    // If the group is expected to be open initially.
    return groupExpandedState[row.groupId];
  });

  return (
    <Box height={700} maxHeight={800} overflow="hidden">
      <div ref={anchorRef}></div>
      <CXDataGrid
        rows={updatedRows}
        columns={columns}
        rowHeight={ROW_HEIGHT}
        getRowId={(row) => row.id}
        getRowClassName={({ row }) => (row.isGroupHeader ? 'group-row-header' : '')}
        hideFooter
        slots={SLOTS}
        slotProps={{
          panel: {
            anchorEl: anchorRef.current,
            placement: 'bottom-end' as const,
            popperOptions: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, ROW_HEIGHT + 5], // adjust if needed
                  },
                },
              ],
            },
          },
        }}
        sx={{
          '& .group-row-header': {
            fontWeight: '400',
            backgroundColor: palette.Background,
            cursor: 'pointer',
          },
          '& .group-row-header .MuiDataGrid-cell': {
            cursor: 'pointer',
          },
        }}
        initialState={{
          pinnedColumns: { left: ['attribute'] },
        }}
      />
    </Box>
  );
};
